import React from 'react';
import { motion } from 'framer-motion';
import { LuGlobe as GlobeIcon, LuStar as StarIcon } from 'react-icons/lu';

const PREVIEW_CARDS = [
  { domain: 'devflow.io',   score: 94 },
  { domain: 'codebase.ai',  score: 89 },
  { domain: 'shipfast.dev', score: 81 },
] as const;

const EXAMPLE_CHIPS = [
  'AI productivity app for developers',
  'Remote team collaboration tool',
  'Eco-friendly sustainable fashion brand',
  'Mental wellness app for professionals',
] as const;

export interface EmptyStateProps {
  onExampleClick: (text: string) => void;
}

export function EmptyState({ onExampleClick }: EmptyStateProps) {
  return (
    <motion.div
      key="empty-state"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex-1 flex flex-col items-center justify-center px-6 py-12"
    >
      {/* Icon ring */}
      <div
        aria-hidden="true"
        className="w-[60px] h-[60px] rounded-full flex items-center justify-center mb-4"
        style={{ border: '1.5px dashed #2a2850' }}
      >
        <GlobeIcon aria-hidden="true" style={{ width: 26, height: 26, color: '#5b4de8' }} />
      </div>

      {/* Heading */}
      <h2 className="text-[18px] font-medium text-[#e8e6ff] text-center mb-2">
        Your results will appear here
      </h2>

      {/* Subtext */}
      <p className="text-[13px] text-[#5a5880] text-center max-w-[340px] leading-[1.6] mb-7">
        Describe what you're building in the sidebar, then hit Regenerate to get scored, available domain suggestions.
      </p>

      {/* "Here's what you'll get" label */}
      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#3e3c60] mb-3">
        Here's what you'll get
      </p>

      {/* Preview cards */}
      <div aria-hidden="true" className="flex gap-[10px] mb-7 opacity-60">
        {PREVIEW_CARDS.map(({ domain, score }) => (
          <div
            key={domain}
            className="w-[130px] bg-[#1a1930] rounded-[10px] px-[14px] py-[10px]"
            style={{ border: '0.5px solid #2a2850' }}
          >
            <p className="text-[13px] font-medium text-[#a89fff] mb-1">{domain}</p>
            <div className="flex items-center gap-[5px]">
              <span
                className="text-[9px] text-[#5ec95e] px-[5px] py-[1px]"
                style={{ background: '#1c3a1c', borderRadius: 4 }}
              >
                available
              </span>
              <span className="flex items-center gap-[2px] text-[9px] text-[#7b70ff]">
                <StarIcon aria-hidden="true" style={{ width: 9, height: 9 }} />
                {score}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Horizontal rule */}
      <hr
        className="w-full max-w-[440px] mb-5"
        style={{ border: 'none', borderTop: '0.5px solid #1e1c35' }}
      />

      {/* "Try an example" label */}
      <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-[#3e3c60] mb-[10px]">
        Try an example
      </p>

      {/* Example chips */}
      <div className="flex flex-wrap justify-center gap-[7px] max-w-[440px]">
        {EXAMPLE_CHIPS.map((text) => (
          <button
            key={text}
            type="button"
            onClick={() => onExampleClick(text)}
            className="text-[11px] px-[13px] py-[6px] rounded-[20px] text-[#7b70ff] hover:text-[#a89fff] hover:bg-[#1e1b40] transition-[background-color,border-color] duration-150 ease-in-out"
            style={{ background: '#13112a', border: '0.5px solid #2a2850' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#5b4de8'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#2a2850'; }}
          >
            {text}
          </button>
        ))}
      </div>
    </motion.div>
  );
}
